import { cn } from "@/lib/utils";
import {
  buildSpellcheckExcerptSegments,
  type CorrectionCategory,
  getCorrectionCategory,
  type DsdEditorCorrectionItem,
  getSpellcheckOriginalSegments,
} from "@/utils/dsd-editor/spellcheck";
import { Switch } from "@/toggle-ui-kit/control/switch";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDown,
  Pen,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  CHANGE_GREEN,
  CHANGE_GREEN_WS,
  CHANGE_RED,
  CHANGE_RED_WS,
  DiffText,
  DsdReviewCorrectionCategoryChip,
} from "./correction-diff";

const groupCorrectionItemsBySection = (items: DsdEditorCorrectionItem[]) => {
  const groups = new Map<string, DsdEditorCorrectionItem[]>();

  for (const item of items) {
    const group = groups.get(item.sectionName);

    if (group) {
      group.push(item);
      continue;
    }

    groups.set(item.sectionName, [item]);
  }

  return [...groups.entries()].map(([sectionName, sectionItems]) => ({
    sectionName,
    items: sectionItems,
  }));
};

// 구분 칩 아이콘 (stroke=currentColor로 칩 색상 상속)
const CorrectionSectionGroup = ({
  sectionName,
  items,
  activeItemId,
  open,
  onOpenChange,
  onSelect,
}: {
  sectionName: string;
  items: DsdEditorCorrectionItem[];
  activeItemId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: DsdEditorCorrectionItem) => void;
}) => (
  // 회색 드롭다운이 항목을 감싸는 컨테이너
  <section className="overflow-hidden rounded-[8px] bg-background-neutral-normal">
    <button
      type="button"
      onClick={() => onOpenChange(!open)}
      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-decorate-neutral/6"
      aria-expanded={open}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {/* 섹션명·개수 — 항목 글씨(13px)의 80% */}
        <p className="caption-2-medium truncate text-label-normal">
          {sectionName}
        </p>
        {/* 섹션별 교정 수 — 파란 글씨 */}
        <span className="shrink-0 caption-2-medium text-primary-color">
          항목 {items.length}개
        </span>
      </div>
      <ChevronDown
        className={cn(
          "size-4 shrink-0 text-label-alternative transition-transform",
          !open && "-rotate-90",
        )}
      />
    </button>
    {open && (
      <div className="px-2 pb-2">
        {/* 항목들을 감싸는 흰색 박스 — 항목보다 살짝 큰 둥근 모서리 */}
        <div className="flex flex-col gap-1.5 rounded-[10px] bg-background-base p-1.5">
          {items.map((item) => (
            <button
              key={`${item.kind}-${item.id}`}
              data-dsd-correction-panel-item={item.id}
              type="button"
              onClick={() => onSelect(item)}
              className={cn(
                "flex w-full min-w-0 items-center justify-between gap-2 rounded-[6px] border px-3 py-4 text-left transition-colors",
                activeItemId === item.id
                  ? "border-status-negative bg-background-neutral-alternative"
                  : "border-line-normal bg-background-base hover:bg-decorate-neutral/6",
              )}
            >
              <span className="min-w-0 flex-1 truncate label-2-medium text-label-normal">
                {item.block.correctedText || item.block.originalText}
              </span>
              {/* 맞춤법(빨강) / 띄어쓰기(주황) 칩 — 우측 */}
              <DsdReviewCorrectionCategoryChip block={item.block} />
            </button>
          ))}
        </div>
      </div>
    )}
  </section>
);

const CorrectionDetailPanel = ({
  item,
  items,
  onPrevious,
  onNext,
}: {
  item: DsdEditorCorrectionItem;
  items: DsdEditorCorrectionItem[];
  onPrevious?: () => void;
  onNext?: () => void;
}) => {
  const changes = item.block.changes;
  // 개수 표시는 구분(섹션)별 위치
  const sectionItems = items.filter(
    (it) => it.sectionName === item.sectionName,
  );
  const sectionIndex = sectionItems.findIndex((it) => it.id === item.id);
  // 원문 red 하이라이트 — 백엔드 changes range 그대로 (어절 단위)
  const originalSegments = useMemo(
    () => getSpellcheckOriginalSegments(item.block),
    [item.block],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* 펜 + 그룹명 + 좌우 네비 (전체 순회) + 섹션 구분선 */}
      <div className="flex shrink-0 flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="shrink-0 rounded-[6px] bg-blue-100 p-2">
              <Pen className="size-3 text-blue-500" />
            </div>
            <p className="label-2-medium truncate text-label-normal">
              {item.sectionName}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="caption-1-medium tabular-nums">
              <span className="text-label-normal">
                {(sectionIndex >= 0 ? sectionIndex : 0) + 1}
              </span>
              <span className="text-label-alternative">
                /{sectionItems.length}
              </span>
            </span>
            <div className="flex divide-x divide-line-normal border border-line-normal">
              <button
                type="button"
                onClick={onPrevious}
                disabled={!onPrevious}
                aria-label="이전 교정"
                className="flex size-10 items-center justify-center text-label-normal transition-colors hover:bg-background-neutral-alternative disabled:opacity-40"
              >
                <ChevronLeft className="size-5" />
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={!onNext}
                aria-label="다음 교정"
                className="flex size-10 items-center justify-center text-label-normal transition-colors hover:bg-background-neutral-alternative disabled:opacity-40"
              >
                <ChevronRight className="size-5" />
              </button>
            </div>
          </div>
        </div>
        <div className="-mx-3 border-b border-line-normal" />
      </div>

      {/* 원문 + 변경 사항 — 하나의 흰 둥근 테두리로 감쌈 (스크롤) */}
      <div className="min-h-0 flex-1 overflow-y-auto rounded-[10px] border border-line-normal bg-background-base">
        {/* 원문 — 상단 sticky */}
        <div className="sticky top-0 z-10 border-b border-line-normal bg-background-base px-3 pb-2 pt-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="caption-2-medium text-label-alternative">원문</p>
            <DsdReviewCorrectionCategoryChip block={item.block} />
          </div>
          <p className="label-2-medium whitespace-pre-wrap text-label-normal">
            <DiffText
              segments={originalSegments}
              changedClassName={CHANGE_RED}
              changedWhitespaceClassName={CHANGE_RED_WS}
            />
          </p>
        </div>
        {/* 변경 사항 — 흰 박스 안에 또 들어간 회색 둥근 영역 */}
        <div className="m-2 rounded-[8px] bg-background-neutral-normal p-2">
          <p className="px-1 pb-2 caption-2-medium text-label-alternative">
            변경 사항
          </p>
          {/* 전체를 감싸는 흰 둥근 박스 + 변경 사항 사이 구분선 */}
          <div className="divide-y divide-line-normal overflow-hidden rounded-[8px] bg-background-base">
            {changes.map((change, changeIndex) => (
              <div
                key={`${changeIndex}-${change.originalRange.offset}-${change.correctedRange.offset}`}
                className="flex flex-col gap-1.5 p-2.5"
              >
                <div className="rounded-[6px] border border-line-normal px-2.5 py-1.5">
                  <p className="mb-1 caption-2-medium text-label-alternative">
                    원문
                  </p>
                  <p className="label-2-medium whitespace-pre-wrap text-label-normal">
                    <DiffText
                      segments={buildSpellcheckExcerptSegments(
                        item.block.originalText,
                        change.originalRange,
                        change.originalRuns,
                      )}
                      changedClassName={CHANGE_RED}
                      changedWhitespaceClassName={CHANGE_RED_WS}
                    />
                  </p>
                </div>
                <ChevronsDown className="mx-auto size-4 shrink-0 text-status-negative" />
                <div className="rounded-[6px] border border-line-normal px-2.5 py-1.5">
                  <p className="mb-1 caption-2-medium text-label-alternative">
                    교정
                  </p>
                  <p className="label-2-medium whitespace-pre-wrap text-label-normal">
                    <DiffText
                      segments={buildSpellcheckExcerptSegments(
                        item.block.correctedText,
                        change.correctedRange,
                        change.correctedRuns,
                      )}
                      changedClassName={CHANGE_GREEN}
                      changedWhitespaceClassName={CHANGE_GREEN_WS}
                    />
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/** 교정 목록(탭·섹션 묶음) ↔ 상세. 머리는 호출측 슬롯 */
export const DsdReviewCorrectionList = ({
  items,
  activeItemId,
  header,
  onPrevious,
  onNext,
  onSelect,
}: {
  items: DsdEditorCorrectionItem[];
  activeItemId: string | null;
  /** 패널 머리(안내 카드). 상세(항목 선택) 여부를 받는다 */
  header: (isDetail: boolean) => ReactNode;
  onPrevious?: () => void;
  onNext?: () => void;
  onSelect: (item: DsdEditorCorrectionItem) => void;
}) => {
  const [sectionOpenByName, setSectionOpenByName] = useState<
    Record<string, boolean>
  >({});
  const [tab, setTab] = useState<"all" | CorrectionCategory>("all");
  // 전체 탭에서 맞춤법 교정 항목만 보기 (띄어쓰기 숨김)
  const [spellingOnly, setSpellingOnly] = useState(false);
  const panelBodyRef = useRef<HTMLDivElement>(null);
  // 맞춤법 수정 있는 것 / 띄어쓰기 변경만 있는 것 분리 (파티션 — 합 = 전체)
  const spellingItems = items.filter(
    (item) => getCorrectionCategory(item.block) === "spelling",
  );
  const spacingItems = items.filter(
    (item) => getCorrectionCategory(item.block) === "spacing",
  );
  // 탭 전체 개수(토글 무관) — '총 N개' 표기용
  const tabTotal =
    tab === "spelling"
      ? spellingItems.length
      : tab === "spacing"
        ? spacingItems.length
        : items.length;
  const visibleItems =
    tab === "spelling"
      ? spellingItems
      : tab === "spacing"
        ? spacingItems
        : spellingOnly
          ? spellingItems
          : items;
  const groupedItems = groupCorrectionItemsBySection(visibleItems);
  const activeItem = activeItemId
    ? (items.find((item) => item.id === activeItemId) ?? null)
    : null;

  useEffect(() => {
    if (!activeItem) return;

    setSectionOpenByName((prev) => ({
      ...prev,
      [activeItem.sectionName]: true,
    }));
  }, [activeItem]);

  const handleSectionOpenChange = (sectionName: string, open: boolean) => {
    setSectionOpenByName((prev) => ({
      ...prev,
      [sectionName]: open,
    }));
  };

  useEffect(() => {
    if (!activeItemId || activeItem) return;

    const target = panelBodyRef.current?.querySelector(
      `[data-dsd-correction-panel-item="${CSS.escape(activeItemId)}"]`,
    );
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeItem, activeItemId]);

  return (
    <aside className="h-full min-h-0 bg-background-base">
      <div className="flex h-full flex-col">
        {header(!!activeItem)}
        {/* 전체 / 맞춤법 / 띄어쓰기 탭 + 총 개수 + 토글 — 목록 뷰에서만 고정 */}
        {!activeItem && items.length > 0 && (
          <>
            <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-line-normal px-3">
              {(
                [
                  { key: "all", label: "전체" },
                  { key: "spelling", label: "맞춤법" },
                  { key: "spacing", label: "띄어쓰기" },
                ] as const
              ).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.key}
                  onClick={() => setTab(item.key)}
                  className={cn(
                    "shrink-0 whitespace-nowrap border-b-2 px-3 py-2 caption-1-medium transition-colors",
                    tab === item.key
                      ? "border-primary-base text-label-normal"
                      : "border-transparent text-label-alternative hover:text-label-normal",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="flex shrink-0 items-center justify-between gap-2 px-3 pb-2 pt-3">
              <p className="label-2-medium text-label-normal">
                총 {tabTotal}개
              </p>
              {tab === "all" && (
                <div className="flex shrink-0 items-center gap-2">
                  <span className="caption-1-medium text-label-alternative">
                    맞춤법 교정 항목만 보기
                  </span>
                  <Switch
                    size={24}
                    active={spellingOnly}
                    onChange={setSpellingOnly}
                    className={spellingOnly ? "bg-orange-500" : undefined}
                    icon={
                      spellingOnly ? (
                        <Check
                          className="size-full text-orange-600"
                          strokeWidth={3.5}
                        />
                      ) : undefined
                    }
                  />
                </div>
              )}
            </div>
          </>
        )}
        <div
          ref={panelBodyRef}
          className={cn(
            "min-h-0 flex-1 p-3",
            activeItem ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          {items.length === 0 ? (
            <div className="flex min-h-[220px] items-center justify-center rounded-[8px] border border-line-normal bg-background-base px-5 text-center">
              <p className="body-2-normal-medium text-label-normal">
                맞춤법 교정이 없습니다.
              </p>
            </div>
          ) : activeItem ? (
            <CorrectionDetailPanel
              item={activeItem}
              items={items}
              onPrevious={onPrevious}
              onNext={onNext}
            />
          ) : (
            <div className="flex flex-col gap-3">
              {groupedItems.length === 0 ? (
                <div className="flex min-h-[160px] items-center justify-center rounded-[8px] border border-line-normal bg-background-base px-5 text-center">
                  <p className="body-2-normal-medium text-label-alternative">
                    해당하는 맞춤법 교정이 없습니다.
                  </p>
                </div>
              ) : (
                groupedItems.map((group) => (
                  <CorrectionSectionGroup
                    key={group.sectionName}
                    sectionName={group.sectionName}
                    items={group.items}
                    activeItemId={activeItemId}
                    open={sectionOpenByName[group.sectionName] ?? true}
                    onOpenChange={(open) =>
                      handleSectionOpenChange(group.sectionName, open)
                    }
                    onSelect={onSelect}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};
